import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdAdd, MdEdit, MdDelete, MdToggleOn, MdToggleOff, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getChannels, createChannel, updateChannel, deleteChannel, toggleChannel } from '../utils/api';

const platformConfig = {
  youtube: {
    icon: <FaYoutube style={{ color: '#ff0000', fontSize: '24px' }} />,
    label: 'YouTube', unitLabel: 'Channel', unitsLabel: 'Channels',
    color: '#ff0000', bg: 'rgba(255,0,0,0.08)', border: 'rgba(255,0,0,0.2)',
    idLabel: 'Channel ID', idPlaceholder: 'UCxxxxxxxxxxxxxxxxxx'
  },
  facebook: {
    icon: <FaFacebook style={{ color: '#1877f2', fontSize: '24px' }} />,
    label: 'Facebook', unitLabel: 'Page', unitsLabel: 'Pages',
    color: '#1877f2', bg: 'rgba(24,119,242,0.08)', border: 'rgba(24,119,242,0.2)',
    idLabel: 'Page ID', idPlaceholder: 'Your Facebook Page ID'
  },
  tiktok: {
    icon: <FaTiktok style={{ color: '#e0e0e0', fontSize: '24px' }} />,
    label: 'TikTok', unitLabel: 'ID', unitsLabel: 'IDs',
    color: '#e0e0e0', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)',
    idLabel: 'TikTok ID', idPlaceholder: '@yourtiktokusername'
  },
  instagram: {
    icon: <FaInstagram style={{ color: '#e1306c', fontSize: '24px' }} />,
    label: 'Instagram', unitLabel: 'Profile', unitsLabel: 'Profiles',
    color: '#e1306c', bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.2)',
    idLabel: 'Instagram Username', idPlaceholder: '@yourinstagramhandle'
  }
};

const emptyForm = {
  name: '', platform: 'youtube', channel_id: '',
  api_key: '', access_token: '',
  upload_frequency: '48 hours', optimal_upload_time: '14:00'
};

const PlatformSection = ({ platform, channels, onEdit, onDelete, onToggle, onAdd, onConnect }) => {
  const [expanded, setExpanded] = useState(true);
  const config = platformConfig[platform];
  const platformChannels = channels.filter(ch => ch.platform === platform);

  return (
    <div style={{
      background: config.bg, border: `1px solid ${config.border}`,
      borderRadius: '16px', marginBottom: '16px', overflow: 'hidden'
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px', cursor: 'pointer'
      }} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {config.icon}
          <div>
            <span style={{ fontWeight: '700', fontSize: '18px', color: 'var(--text-primary)' }}>
              {config.label}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', marginLeft: '10px' }}>
              {platformChannels.length} {platformChannels.length === 1 ? config.unitLabel : config.unitsLabel}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-primary btn-sm"
            onClick={(e) => { e.stopPropagation(); onConnect(platform); }}>
            <MdAdd /> Connect {config.unitLabel}
          </button>
          {expanded
            ? <MdExpandLess style={{ color: 'var(--text-secondary)', fontSize: '24px' }} />
            : <MdExpandMore style={{ color: 'var(--text-secondary)', fontSize: '24px' }} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 24px 20px' }}>
          {platformChannels.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '32px',
              background: 'var(--bg-dark)', borderRadius: '12px',
              border: '1px dashed var(--border)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                No {config.unitsLabel.toLowerCase()} added yet
              </p>
              <button className="btn btn-primary btn-sm" onClick={() => onConnect(platform)}>
                <MdAdd /> Connect Your First {config.unitLabel}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {platformChannels.map((channel) => (
                <div key={channel.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 20px', background: 'var(--bg-card)',
                  borderRadius: '12px', border: '1px solid var(--border)',
                  transition: 'all 0.2s ease'
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = config.border;
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                      {channel.name}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {config.idLabel}: {channel.channel_id}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <span className="badge badge-secondary">{channel.upload_frequency}</span>
                      <span className="badge badge-secondary">{channel.optimal_upload_time}</span>
                      <span className={`badge ${channel.is_active ? 'badge-success' : 'badge-secondary'}`}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {channel.access_token && (
                        <span className="badge badge-success">OAuth Connected</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => onToggle(channel.id)}>
                      {channel.is_active
                        ? <MdToggleOn style={{ color: '#00c853', fontSize: '20px' }} />
                        : <MdToggleOff style={{ fontSize: '20px' }} />}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => onEdit(channel)}>
                      <MdEdit />
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => onDelete(channel.id)}>
                      <MdDelete />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ChannelManager = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchChannels();
    checkOAuthCallback();
  }, []);

  const checkOAuthCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get('oauth');
    const status = params.get('status');
    if (oauth && status) {
      if (status === 'success') {
        toast.success(`${oauth.charAt(0).toUpperCase() + oauth.slice(1)} connected successfully!`);
        fetchChannels();
      } else {
        toast.error(`Failed to connect ${oauth}`);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const connectOAuth = async (platform) => {
    try {
      const response = await fetch(`https://vantage-production-4475.up.railway.app/api/oauth/${platform}/auth`);
      const data = await response.json();
      if (data.data?.auth_url) {
        window.open(data.data.auth_url, '_blank', 'width=600,height=600');
      } else {
        toast.error('Failed to get auth URL');
      }
    } catch (error) {
      toast.error('Failed to connect: ' + error.message);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await getChannels();
      setChannels(res.data.data || []);
    } catch (error) {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.channel_id) {
      toast.error('Name and ID are required');
      return;
    }
    setSaving(true);
    try {
      if (editingChannel) {
        await updateChannel(editingChannel.id, form);
        toast.success('Updated successfully');
      } else {
        await createChannel(form);
        toast.success('Added successfully');
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingChannel(null);
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setForm({
      name: channel.name, platform: channel.platform,
      channel_id: channel.channel_id, api_key: channel.api_key || '',
      access_token: channel.access_token || '',
      upload_frequency: channel.upload_frequency,
      optimal_upload_time: channel.optimal_upload_time
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this?')) return;
    try {
      await deleteChannel(id);
      toast.success('Deleted successfully');
      fetchChannels();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleChannel(id);
      fetchChannels();
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  const handleAdd = (platform) => {
    setEditingChannel(null);
    setForm({ ...emptyForm, platform });
    setShowModal(true);
  };

  const currentConfig = platformConfig[form.platform];

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading"><div className="spinner"></div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Platform Manager</h1>
        <p>Manage all your channels, pages and profiles across every platform</p>
      </div>

      {/* OAuth Connection Section */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <span className="card-title">Connect Platforms with OAuth</span>
          <span className="badge badge-success">One Click Connect</span>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Connect your accounts securely. Your channels will be automatically imported after connecting.
        </p>
        <div className="grid-4">
          {[
            { platform: 'youtube', label: 'YouTube', icon: <FaYoutube style={{ fontSize: '24px', color: '#ff0000' }} />, bg: 'rgba(255,0,0,0.08)', border: 'rgba(255,0,0,0.2)', color: '#ff0000' },
            { platform: 'facebook', label: 'Facebook', icon: <FaFacebook style={{ fontSize: '24px', color: '#1877f2' }} />, bg: 'rgba(24,119,242,0.08)', border: 'rgba(24,119,242,0.2)', color: '#1877f2' },
            { platform: 'instagram', label: 'Instagram', icon: <FaInstagram style={{ fontSize: '24px', color: '#e1306c' }} />, bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.2)', color: '#e1306c' },
            { platform: 'tiktok', label: 'TikTok', icon: <FaTiktok style={{ fontSize: '24px', color: '#e0e0e0' }} />, bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', color: '#e0e0e0' }
          ].map((p) => (
            <button key={p.platform} onClick={() => connectOAuth(p.platform)}
              style={{
                background: p.bg, border: `2px solid ${p.border}`,
                borderRadius: '14px', padding: '20px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '10px', transition: 'all 0.3s ease', fontFamily: 'Inter, sans-serif'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${p.border}`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {p.icon}
              <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
                {p.label}
              </span>
              <span style={{
                fontSize: '11px', color: p.color, fontWeight: '600',
                background: p.bg, padding: '3px 10px',
                borderRadius: '10px', border: `1px solid ${p.border}`
              }}>
                Connect
              </span>
            </button>
          ))}
        </div>
      </div>

      {Object.keys(platformConfig).map(platform => (
        <PlatformSection
          key={platform} platform={platform} channels={channels}
          onEdit={handleEdit} onDelete={handleDelete}
          onToggle={handleToggle} onAdd={handleAdd}
          onConnect={connectOAuth}
        />
      ))}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {currentConfig?.icon}
                {editingChannel ? `Edit ${currentConfig?.unitLabel}` : `Add ${currentConfig?.unitLabel}`}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>X</button>
            </div>
            <div className="form-group">
              <label className="form-label">Platform</label>
              <select className="form-select" value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                disabled={!!editingChannel}>
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{currentConfig?.unitLabel} Name *</label>
              <input className="form-control"
                placeholder={`e.g. My ${currentConfig?.label} ${currentConfig?.unitLabel}`}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{currentConfig?.idLabel} *</label>
              <input className="form-control" placeholder={currentConfig?.idPlaceholder}
                value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">API Key</label>
              <input className="form-control" type="password" placeholder="Paste your API key here"
                value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Access Token</label>
              <input className="form-control" type="password" placeholder="Paste your access token here"
                value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Upload Frequency</label>
                <select className="form-select" value={form.upload_frequency}
                  onChange={(e) => setForm({ ...form, upload_frequency: e.target.value })}>
                  <option value="daily">Daily</option>
                  <option value="48 hours">Every 48 Hours</option>
                  <option value="weekly">Weekly</option>
                  <option value="twice weekly">Twice Weekly</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Optimal Upload Time</label>
                <input className="form-control" type="time" value={form.optimal_upload_time}
                  onChange={(e) => setForm({ ...form, optimal_upload_time: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editingChannel ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelManager;